import * as React from 'react';
import { Copy, Info } from 'lucide-react';
import type { CheckResult } from '@clawhire/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/select';
import { SkeletonLines } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useMessageTemplates, useRenderedMessage } from '@/pages/submissions/api';

export const DEFAULT_TEMPLATE_KEY = 'submission_to_intermediary';

export interface DraftEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submissionId: string;
  /** Shown under the title, e.g. the submission title. */
  context?: string;
}

/**
 * Renders a message template for this submission (`POST /messages/render`) and lets the user copy
 * subject and body. Nothing is sent from ClawHire in v1.
 */
export function DraftEmailDialog({ open, onOpenChange, submissionId, context }: DraftEmailDialogProps) {
  const [templateKey, setTemplateKey] = React.useState(DEFAULT_TEMPLATE_KEY);
  const templates = useMessageTemplates(open);
  const rendered = useRenderedMessage(templateKey, submissionId, open);

  const options = React.useMemo(() => {
    const list = (templates.data ?? []).map((t) => ({ value: t.key, label: t.name }));
    return list.some((o) => o.value === DEFAULT_TEMPLATE_KEY)
      ? list
      : [{ value: DEFAULT_TEMPLATE_KEY, label: 'Submission to the intermediary' }, ...list];
  }, [templates.data]);

  const copy = async (what: 'subject' | 'body', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(what === 'subject' ? 'Subject copied' : 'Body copied');
    } catch {
      toast.error('Could not copy to the clipboard');
    }
  };

  const checks = rendered.data?.checks;
  const problems: CheckResult[] = checks ? [...checks.errors, ...checks.warnings] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Draft e-mail</DialogTitle>
          <DialogDescription>
            {context ? <span className="block truncate">{context}</span> : null}
            Rendered from the template with the submission data; copy it into your mail client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="draft-email-template">Template</Label>
            <SimpleSelect
              id="draft-email-template"
              value={templateKey}
              onChange={(v) => v && setTemplateKey(v)}
              options={options}
              className="w-full sm:w-80"
            />
            {templates.isError ? (
              <p className="text-xs text-destructive">
                Could not load the templates: {templates.error.message}
              </p>
            ) : null}
          </div>

          {rendered.isLoading ? (
            <SkeletonLines lines={5} />
          ) : rendered.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Could not render the message: {rendered.error.message}
            </p>
          ) : rendered.data ? (
            <>
              {problems.length > 0 ? (
                <ul className="space-y-1" aria-label="Checks">
                  {problems.map((c, i) => (
                    <li
                      key={`${c.code}-${i}`}
                      role={c.level === 'error' ? 'alert' : undefined}
                      className={
                        c.level === 'error' ? 'text-sm font-medium text-destructive' : 'text-sm text-warning'
                      }
                    >
                      {c.level === 'error' ? 'Error' : 'Warning'}: {c.message}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Checks passed: no forbidden terms, all languages listed.
                </p>
              )}

              <CopyField
                id="draft-email-subject"
                label="Subject"
                value={rendered.data.subject}
                rows={2}
                onCopy={() => copy('subject', rendered.data.subject)}
              />
              <CopyField
                id="draft-email-body"
                label="Body"
                value={rendered.data.body}
                rows={14}
                onCopy={() => copy('body', rendered.data.body)}
              />
            </>
          ) : null}

          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Nothing is sent from ClawHire in v1. Paste the text into your mail client and attach the profile
            PDF.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyField({
  id,
  label,
  value,
  rows,
  onCopy,
}: {
  id: string;
  label: string;
  value: string;
  rows: number;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <Button variant="outline" size="sm" onClick={onCopy} aria-label={`Copy ${label.toLowerCase()}`}>
          <Copy aria-hidden="true" /> Copy
        </Button>
      </div>
      <Textarea id={id} value={value} readOnly rows={rows} className="font-mono text-xs" />
    </div>
  );
}
